import { useState, useEffect } from 'react';
import { Play, Square, RefreshCw, Activity, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { getAppsStatus, triggerAppAction, getAppLogs, runHealthCheck } from '../lib/api';
import type { AppStatus } from '../types';

export default function AppsManager() {
  const [apps, setApps] = useState<AppStatus[]>([]);
  const [selectedApp, setSelectedApp] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);

  useEffect(() => {
    fetchApps();
  }, []);

  useEffect(() => {
    if (selectedApp) {
      fetchLogs(selectedApp);
    }
  }, [selectedApp]);

  async function fetchApps(): Promise<void> {
    try {
      const data = await getAppsStatus();
      setApps(data);
    } catch {
      setApps([]);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchLogs(appName: string): Promise<void> {
    try {
      const data = await getAppLogs(appName, 10);
      setLogs(data);
    } catch {
      setLogs([]);
    }
  }

  async function handleAction(appName: string, action: string): Promise<void> {
    setIsActionLoading(true);
    try {
      await triggerAppAction(appName, action);
      await fetchApps();
      if (selectedApp === appName) {
        await fetchLogs(appName);
      }
    } catch {
      void 0;
    } finally {
      setIsActionLoading(false);
    }
  }

  async function handleHealthCheck(appName: string): Promise<void> {
    setIsActionLoading(true);
    try {
      const result = await runHealthCheck(appName);
      setLogs((prev) => [`Health check: ${result.status}`, ...prev]);
    } catch {
      setLogs((prev) => ['Health check: failed', ...prev]);
    } finally {
      setIsActionLoading(false);
    }
  }

  function getStatusBadge(status: AppStatus['status']): React.ReactNode {
    switch (status) {
      case 'running':
        return (
          <span className="badge badge-success">
            <CheckCircle className="mr-1 h-3 w-3" />
            Running
          </span>
        );
      case 'stopped':
        return (
          <span className="badge badge-muted">
            <Square className="mr-1 h-3 w-3" />
            Stopped
          </span>
        );
      case 'error':
        return (
          <span className="badge badge-destructive">
            <AlertCircle className="mr-1 h-3 w-3" />
            Error
          </span>
        );
    }
  }

  function formatUptime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h`;
    return `${hours}h ${Math.floor((seconds % 3600) / 60)}m`;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Apps Manager</h1>
        <p className="text-sm text-muted-foreground">Manage Uploader and SEO Agent services</p>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {apps.map((app) => (
            <div key={app.name} className="card">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">{app.name}</h3>
                  <div className="mt-1 flex items-center gap-2">
                    {getStatusBadge(app.status)}
                    <span className="text-xs text-muted-foreground">v{app.version}</span>
                  </div>
                </div>
                <Activity className="h-5 w-5 text-muted-foreground" />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Uptime</p>
                  <p className="font-medium text-foreground">{formatUptime(app.uptime)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Last Check</p>
                  <p className="font-medium text-foreground">
                    {new Date(app.lastCheck).toLocaleTimeString()}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {app.status === 'running' ? (
                  <button
                    onClick={() => handleAction(app.name, 'stop')}
                    disabled={isActionLoading}
                    className="btn-secondary"
                  >
                    <Square className="mr-1.5 h-4 w-4" />
                    Stop
                  </button>
                ) : (
                  <button
                    onClick={() => handleAction(app.name, 'start')}
                    disabled={isActionLoading}
                    className="btn-primary"
                  >
                    <Play className="mr-1.5 h-4 w-4" />
                    Start
                  </button>
                )}
                <button
                  onClick={() => handleAction(app.name, 'restart')}
                  disabled={isActionLoading}
                  className="btn-secondary"
                >
                  <RefreshCw className="mr-1.5 h-4 w-4" />
                  Restart
                </button>
                <button
                  onClick={() => handleHealthCheck(app.name)}
                  disabled={isActionLoading}
                  className="btn-secondary"
                >
                  <Activity className="mr-1.5 h-4 w-4" />
                  Health Check
                </button>
              </div>

              <div className="mt-4">
                <button
                  onClick={() => setSelectedApp(selectedApp === app.name ? null : app.name)}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                >
                  <FileText className="h-4 w-4" />
                  {selectedApp === app.name ? 'Hide Logs' : 'Show Recent Logs'}
                </button>

                {selectedApp === app.name && (
                  <div className="mt-2 rounded-lg bg-muted p-3 font-mono text-xs text-muted-foreground max-h-48 overflow-y-auto">
                    {logs.length === 0 ? (
                      <p>No recent logs</p>
                    ) : (
                      logs.map((log, index) => (
                        <p key={index} className="py-0.5">
                          {log}
                        </p>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
